import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiButtonComponent,
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSearchPanelComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { FilterPanelComponent } from '../../../components/library/filter-panel/filter-panel.component';
import {
    CompleteAppointmentShuntCommand,
    CompleteEmptyCheckInShuntCommand,
    CompleteEmptyCheckOutShuntCommand,
    YardManagementClient,
    ShuntMoveDto,
    ShuntMoveViewDto,
    YardEntityType,
    ShuntPriority,
    PermissionType,
    AccessType,
    AuthService,
} from '@services';
import EnumUtils from '../../../shared/enum-utils';
import { CommonModule, NgClass } from '@angular/common';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { DATE_TIME_FORMAT } from 'src/app/shared/constants';
import { UserAvatarEmailComponent } from 'src/app/components/utils/user-avatar-email/user-avatar-email.component';
import { FilterConfig, GridPageComponent } from '../../common/grid-page.component';
import { ShuntPriorityPipe } from 'src/app/pipes/shunt-priority.pipe';
import { NotesComponent, NoteType } from 'src/app/components/library/notes/notes.component';
import notify from 'devextreme/ui/notify';
import { ShuntMoveFormComponent } from './shunt-move-form/shunt-move-form.component';

@Component({
    selector: 'app-shunt-moves',
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        DxoSortingComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoSearchPanelComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxiButtonComponent,
        DxButtonComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        NgClass,
        FilterPanelComponent,
        DxFormComponent,
        ShuntPriorityPipe,
        UserAvatarEmailComponent,
        ShuntMoveFormComponent,
        NotesComponent,
    ],
    providers: [ShuntPriorityPipe],
    templateUrl: './shunt-moves.component.html',
})
export class ShuntMovesComponent extends GridPageComponent<ShuntMoveViewDto> implements OnInit
{
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    private shuntPriorityPipe = inject(ShuntPriorityPipe);
    private yardManagementClient = inject(YardManagementClient);
    protected auth = inject(AuthService);

    protected override exportName: string = 'Shunt Moves Report';
    protected offset: number = 100;
    protected readonly NoteType = NoteType;
    dateTimeFormat = DATE_TIME_FORMAT;
    formData: ShuntMoveDto = {} as ShuntMoveDto;
    isShuntMovePopupOpened = false;
    isCompleteShuntButtonVisible = false;
    pendingNumber: string | undefined;
    pendingNoteType: NoteType | undefined;
    pendingYardEntityType: YardEntityType | undefined;
    dataSource: ShuntMoveViewDto[] = [];

    shuntPriorityOptions = EnumUtils.getEnumOptions(ShuntPriority, (v) =>
        this.shuntPriorityPipe.transform(v),
    );

    protected override filterConfig: FilterConfig[] = [
        { field: 'number', op: 'contains' },
        { field: 'carrier', op: 'contains' },
        { field: 'trailerNumber', op: 'contains' },
        { field: 'currentShuntLocation', op: 'contains' },
        { field: 'newShuntLocation', op: 'contains' },
        { field: 'notes', op: 'contains' },
        { field: 'shuntPriority', op: '=' },
        {
            field: 'employee.name',
            op: 'contains',
            customFilter: this.nameOrEmailFilter,
        },
        { field: 'created', op: '>=' },
    ];

    constructor() {
        super();
        this.init(ShuntMoveViewDto);
    }

    shuntPrioritySortValue(rowData: ShuntMoveViewDto): number {
        switch (rowData?.shuntPriority) {
            case ShuntPriority.High:
                return 1;
            case ShuntPriority.Medium:
                return 2;
            case ShuntPriority.Low:
                return 3;
            default:
                return Number.MAX_SAFE_INTEGER;
        }
    }

    ngOnInit(): void {
        this.isCompleteShuntButtonVisible = this.auth.checkPermission(PermissionType.ShuntManagement, AccessType.Manage);
        this.loadData();
    }

    loadData() {
        this.yardManagementClient.getShuntMoves().subscribe({
            next: (data) => {
                this.dataSource = data;
            },
        });
    }

    protected getShuntPriorityBadgeClass(status: ShuntPriority): string {
        return EnumUtils.getShuntPriorityBadgeClass(status);
    }

    async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        this.openShuntMovePopup(e.data);
    }

    override onView = (e: any) => {
        this.openShuntMovePopup(e.row.data);
    };

    private openShuntMovePopup(data: ShuntMoveViewDto): void {
        this.formData = new ShuntMoveDto({
            location: data.newLocation,
            locationId: data.newShuntLocationId,
            priority: data.shuntPriority,
            note: data.notes,
        });

        this.isShuntMovePopupOpened = true;
    }

    completeShunt(number: string, type: YardEntityType): void {
        this.pendingNumber = number;
        this.pendingYardEntityType = type;

        switch (type) {
            case YardEntityType.Appointment:
                this.pendingNoteType = NoteType.Appointment;
                break;
            case YardEntityType.EmptyCheckIn:
                this.pendingNoteType = NoteType.EmptyCheckIn;
                break;
            case YardEntityType.EmptyCheckOut:
                this.pendingNoteType = NoteType.EmptyCheckOut;
                break;
        }

        this.notesComponent.openCreatePopup();
    }

    onNoteSaved() {
        if (!this.pendingNumber || !this.pendingYardEntityType) {
            return;
        }

        let number = this.pendingNumber;
        let type = this.pendingYardEntityType;
        this.pendingNumber = undefined;
        this.pendingYardEntityType = undefined;
        this.pendingNoteType = undefined;
        this.save(number, type);
    }

    onNoteCanceled() {
        this.pendingNumber = undefined;
        this.pendingYardEntityType = undefined;
        this.pendingNoteType = undefined;
    }

    save(number: string, type: YardEntityType) {
        let request$;

        switch (type) {
            case YardEntityType.Appointment:
                request$ = 
                    this.yardManagementClient.completeAppointmentShunt(
                        new CompleteAppointmentShuntCommand({ number }),
                    );
                break;
            case YardEntityType.EmptyCheckIn:
                request$ = 
                    this.yardManagementClient.completeEmptyCheckInShunt(
                        new CompleteEmptyCheckInShuntCommand({ number }),
                    );
                break;
            case YardEntityType.EmptyCheckOut:
                request$ =
                    this.yardManagementClient.completeEmptyCheckOutShunt(
                        new CompleteEmptyCheckOutShuntCommand({ number }),
                    );
                break;
        }

        request$.subscribe({
            next: async (number) => {
                this.loadData();
                await this.dataGrid.instance.refresh();
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message || 'Failed to complete shunt';
                notify(msg, 'error', 3000);
            },
        });
    }
}
